import { useState, useEffect, useMemo, useImperativeHandle, forwardRef } from 'react';
import { User } from 'lucide-react';
import type { ExternalUserFormState } from '../../hooks/useExternalUserFormWizard';
import { PhoneInput } from '@/Components/shared/PhoneInput';
import { EmailInput } from '@/Components/shared/EmailInput';
import { FormInput } from '@/Components/shared/FormInput';
import { FormSelect } from '@/Components/shared/FormSelect';
import { Tooltip } from '@/Components/ui/Tooltip';
import { SocietySelector } from '../SocietySelector';
import { ChildSocietiesMultiSelect } from '../ChildSocietiesMultiSelect';
import { useSocietyChildren } from '@/features/societies/hooks/useSocieties';
import type { WizardStepHandle } from './types';

interface GroupSociety {
  uuid: string;
  name: string;
  code?: string;
  city?: string;
}

interface Step1InformationsProps {
  formData: ExternalUserFormState;
  updateFormData: (updates: Partial<ExternalUserFormState>) => void;
  onNext: () => void;
  shouldShowSocietyStep?: boolean;
  isSocietyLocked?: boolean;
  allowedSocietyIds?: string[];
  groupSocieties?: GroupSociety[];
  onCanProceedChange?: (canProceed: boolean) => void;
}

export const Step1Informations = forwardRef<WizardStepHandle, Step1InformationsProps>(({
  formData,
  updateFormData,
  onNext,
  shouldShowSocietyStep = false,
  isSocietyLocked = false,
  allowedSocietyIds,
  groupSocieties,
  onCanProceedChange,
}, ref) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [emailValidation, setEmailValidation] = useState<{ error: string | null; isChecking: boolean }>({ error: null, isChecking: false });

  const { data: fetchedChildren = [], isLoading: isLoadingChildren } = useSocietyChildren(
    !groupSocieties && shouldShowSocietyStep ? (formData.primarySocietyId || '') : ''
  );

  const effectiveAdditionalSocieties = useMemo(() => {
    if (groupSocieties) {
      return groupSocieties.filter(s => s.uuid !== formData.primarySocietyId);
    }
    return fetchedChildren;
  }, [groupSocieties, formData.primarySocietyId, fetchedChildren]);

  useEffect(() => {
    if (!shouldShowSocietyStep) return;
    if (formData.primarySocietyId && formData.additionalSocietyIds.length > 0) {
      const validIds = effectiveAdditionalSocieties.map(c => c.uuid);
      const stillValid = formData.additionalSocietyIds.filter(id =>
        validIds.includes(id)
      );

      if (stillValid.length !== formData.additionalSocietyIds.length) {
        updateFormData({ additionalSocietyIds: stillValid });
      }
    }
  }, [effectiveAdditionalSocieties, formData.primarySocietyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasAdditionalSocieties = effectiveAdditionalSocieties.length > 0;

  // Remonter canProceed au parent
  useEffect(() => {
    onCanProceedChange?.(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field: keyof ExternalUserFormState, value: string) => {
    updateFormData({ [field]: value });
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleNext = () => {
    const newErrors: Record<string, string> = {};

    if (shouldShowSocietyStep && !isSocietyLocked && !formData.primarySocietyId) {
      newErrors.primarySocietyId = 'Veuillez sélectionner une société';
    }

    if (!formData.firstname.trim()) {
      newErrors.firstname = 'Le prénom est obligatoire';
    }
    if (!formData.lastname.trim()) {
      newErrors.lastname = 'Le nom est obligatoire';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est obligatoire';
    } else if (emailValidation.isChecking) {
      newErrors.email = 'Vérification de l\'email en cours...';
    } else if (emailValidation.error) {
      newErrors.email = emailValidation.error;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onNext();
  };

  useImperativeHandle(ref, () => ({
    handleNext,
  }));

  return (
    <div className="tw-max-w-6xl tw-mx-auto tw-p-4" data-testid="step-informations">

      {/* Bloc Société (conditionnel) */}
      {shouldShowSocietyStep && (!isSocietyLocked || hasAdditionalSocieties) && (
        <div id="external-user-society-block" className="tw-p-6 tw-space-y-6">
          {!isSocietyLocked && (
            <SocietySelector
              value={formData.primarySocietyId}
              onChange={(societyId) =>
                updateFormData({
                  primarySocietyId: societyId || '',
                  additionalSocietyIds: [],
                })
              }
              error={errors.primarySocietyId}
              allowedSocietyIds={allowedSocietyIds}
            />
          )}

          {formData.primarySocietyId && (
            <>
              {!groupSocieties && isLoadingChildren ? (
                <div className="tw-flex tw-items-center tw-gap-2 tw-text-gray-500 tw-py-4">
                  <div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-2 tw-border-gray-300 tw-border-t-blue-600" />
                  <span className="tw-text-sm">Chargement des sociétés du groupe...</span>
                </div>
              ) : hasAdditionalSocieties ? (
                <ChildSocietiesMultiSelect
                  societies={effectiveAdditionalSocieties}
                  selectedIds={formData.additionalSocietyIds}
                  onChange={(ids) => updateFormData({ additionalSocietyIds: ids })}
                />
              ) : null}
            </>
          )}
        </div>
      )}

      {/* Bloc Informations personnelles */}
      <div className={`tw-p-6 tw-space-y-5${shouldShowSocietyStep && (!isSocietyLocked || hasAdditionalSocieties) ? ' tw-border-t tw-border-gray-100 tw-pt-6 tw-mt-6' : ''}`}>
        {/* Heading unique */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
          <User className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
          <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">Informations utilisateur</h4>
        </div>

        {/* Carte 1 - Identité */}
        <div id="external-user-identity-block" className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
          <p className="tw-text-sm tw-text-gray-500 tw-mb-0">Renseignez l'identité de l'utilisateur<Tooltip content="Civilité, prénom et nom tels qu'ils apparaîtront dans l'application" /></p>
          <div className="tw-space-y-4">
            <FormSelect
              value={formData.civility || ''}
              onChange={(value) => handleChange('civility', value)}
              options={[
                { value: '', label: '-' },
                { value: 'M.', label: 'M.' },
                { value: 'Mme', label: 'Mme' },
              ]}
              label="Civilité"
              tooltip="Titre de civilité de l'utilisateur"
              dataTestId="select-civility"
            />

            <FormInput
              value={formData.firstname}
              onChange={(value) => handleChange('firstname', value.toUpperCase())}
              label="Prénom"
              placeholder="JEAN"
              required
              error={errors.firstname}
              valid={!!formData.firstname.trim()}
              tooltip="Prénom de l'utilisateur tel qu'il apparaîtra dans l'application"
              dataTestId="input-firstname"
            />

            <FormInput
              value={formData.lastname}
              onChange={(value) => handleChange('lastname', value.toUpperCase())}
              label="Nom"
              placeholder="Dupont"
              required
              error={errors.lastname}
              valid={!!formData.lastname.trim()}
              tooltip="Nom de famille de l'utilisateur tel qu'il apparaîtra dans l'application"
              dataTestId="input-lastname"
            />
          </div>
        </div>

        {/* Carte 2 - Coordonnées */}
        <div id="external-user-contact-block" className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
          <p className="tw-text-sm tw-text-gray-500 tw-mb-0">Coordonnées de contact de l'utilisateur<Tooltip content="L'email servira d'identifiant de connexion pour l'utilisateur" /></p>
          <div className="tw-space-y-4">
            <EmailInput
              value={formData.email}
              onChange={(value) => handleChange('email', value)}
              label="Email"
              placeholder="jean.dupont@example.com"
              required
              checkEndpoint="/external-users-v2/check-email"
              externalError={errors.email}
              tooltip="Adresse email de l'utilisateur. Elle servira d'identifiant de connexion."
              onValidationChange={setEmailValidation}
              dataTestId="input-email"
            />

            <PhoneInput
              value={formData.phone || ''}
              onChange={(value) => handleChange('phone', value)}
              tooltip="Numéro de téléphone de l'utilisateur"
              dataTestId="input-phone"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

Step1Informations.displayName = 'Step1Informations';
